import { Routes, Route, useSearchParams, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Suspense, useState, useCallback, useEffect } from 'react';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { useAuth } from '@/context/AuthContext';
import { NavigationProvider } from '@/context/NavigationContext';
import { DonationProvider } from '@/context/DonationContext';
import { UpdateAnimationProvider, useUpdateAnimation } from '@/context/UpdateAnimationContext';
import { VisualEffectsProvider } from '@/context/VisualEffectsContext';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import { useNotificationNavigation } from '@/hooks/useNotificationNavigation';
import { isLeaderOrAdmin } from '@/utils/permissions';
import { signInUrlFor } from '@/utils/returnTo';
import { IndexRedirect } from '@/components/IndexRedirect';
import { AppShell } from '@/components/layout';
import { Header } from '@/components/Header';
import { BottomNavigation } from '@/components/BottomNavigation';
import { ToastContainer } from '@/components/Toast';
import { DonationBadge } from '@/components/DonationBadge';
import { SpiritLoader } from '@/components/loading';
import { InstallBanner } from '@/components/pwa';
import { ForceRefreshModal } from '@/components/ForceRefreshModal';
import { UpdateBadge } from '@/components/UpdateBadge';
import { ErrorBoundary } from '@/components/errors/ErrorBoundary';
import { FeatureErrorFallback } from '@/components/errors/FeatureErrorFallback';

// Lazy load route components for code splitting
// Uses lazyWithRetry to handle stale chunk failures after deployments:
// retries once, then auto-reloads to pick up fresh asset references
const StudySection = lazyWithRetry(() =>
  import('@/features/study/StudySection').then((m) => ({ default: m.StudySection }))
);
const ChatSection = lazyWithRetry(() =>
  import('@/features/chat/ChatSection').then((m) => ({ default: m.ChatSection }))
);
const LibrarySection = lazyWithRetry(() =>
  import('@/features/library/LibrarySection').then((m) => ({ default: m.LibrarySection }))
);
const PrayersSection = lazyWithRetry(() =>
  import('@/features/prayers/PrayersSection').then((m) => ({ default: m.PrayersSection }))
);
const ProfileSection = lazyWithRetry(() =>
  import('@/features/profile/ProfileSection').then((m) => ({ default: m.ProfileSection }))
);
const RegisterPage = lazyWithRetry(() =>
  import('@/features/auth/RegisterPage').then((m) => ({ default: m.RegisterPage }))
);
const ProfileSetupPage = lazyWithRetry(() =>
  import('@/features/auth/ProfileSetupPage').then((m) => ({ default: m.ProfileSetupPage }))
);
const MfaRequiredScreen = lazyWithRetry(() =>
  import('@/features/auth/components/MfaRequiredScreen').then((m) => ({
    default: m.MfaRequiredScreen,
  }))
);
const AdminPage = lazyWithRetry(() =>
  import('@/features/admin/AdminPage').then((m) => ({ default: m.AdminPage }))
);
const PolicyPage = lazyWithRetry(() =>
  import('@/features/legal/PolicyPage').then((m) => ({ default: m.PolicyPage }))
);
const LegalHubPage = lazyWithRetry(() =>
  import('@/features/legal/LegalHubPage').then((m) => ({ default: m.LegalHubPage }))
);
const ReactivationScreen = lazyWithRetry(() =>
  import('@/features/auth/ReactivationScreen').then((m) => ({ default: m.ReactivationScreen }))
);

// Loading fallback component
function PageLoader() {
  return (
    <div className="h-full flex items-center justify-center bg-slate-50 text-slate-400">
      <div className="animate-pulse">Loading...</div>
    </div>
  );
}

// Components that should not render on admin page
function NonAdminComponents() {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) {
    return null;
  }
  return (
    <>
      {/* Subtle update badge for admin-suggested refreshes (bottom-left) */}
      <UpdateBadge />
      {/* Force refresh modal (non-dismissible, admin-triggered) */}
      <ForceRefreshModal />
      {/* iOS Safari PWA install prompt */}
      <InstallBanner />
    </>
  );
}

// Protected route wrapper for admin-only access
function AdminRoute() {
  const { authState } = useAuth();
  const location = useLocation();

  // Hide the HTML preloader on admin page (normally done by SpiritLoader)
  useEffect(() => {
    const preloader = document.getElementById('spirit-preloader');
    if (preloader) {
      preloader.style.opacity = '0';
      preloader.style.pointerEvents = 'none';
      setTimeout(() => {
        preloader.style.display = 'none';
      }, 300);
    }
  }, []);

  // Still loading - show minimal loading state (no SpiritLoader for admin)
  if (authState.status === 'loading') {
    return (
      <div className="h-app flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }

  // Not authenticated
  // Not authenticated: sign in first, then come back here (for example from an email link)
  if (authState.status !== 'authenticated') {
    return <Navigate to={signInUrlFor(location)} replace />;
  }

  // Check if user is leader or admin
  if (!isLeaderOrAdmin(authState.profile)) {
    return (
      <div className="h-app flex items-center justify-center bg-slate-50">
        <div className="max-w-md text-center p-8">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-slate-800 font-['Merriweather'] mb-2">
            Access Denied
          </h1>
          <p className="text-slate-500 mb-6">
            You don't have permission to access the admin panel. Only leaders and administrators can
            view this page.
          </p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-[#8e4585] text-white rounded-full font-bold hover:bg-[#753a6f] transition-colors"
          >
            Go Back Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <AdminPage />
    </Suspense>
  );
}

// Layout wrapper for authenticated main app sections
function MainLayout() {
  const { authState } = useAuth();
  const { isShowingUpdateAnimation } = useUpdateAnimation();
  const [searchParams] = useSearchParams();
  // If update animation is/was showing, skip the loader entirely
  const [loaderRemoved, setLoaderRemoved] = useState(() => isShowingUpdateAnimation);

  // When update animation starts, mark loader as removed so it never shows
  useEffect(() => {
    if (isShowingUpdateAnimation) {
      setLoaderRemoved(true);
    }
  }, [isShowingUpdateAnimation]);

  // Ultimate failsafe at the layout level - if loader hasn't been removed after 45 seconds,
  // force remove it. This is the last line of defense against stuck loading screens.
  useEffect(() => {
    // Skip if loader is already removed or update animation is showing
    if (loaderRemoved || isShowingUpdateAnimation) return;

    const ultimateFailsafeTimeout = setTimeout(() => {
      if (!loaderRemoved) {
        console.warn(
          '[MainLayout] Ultimate failsafe triggered - forcing loader removal after 45 seconds'
        );
        setLoaderRemoved(true);

        // Also hide the HTML preloader if it's somehow still visible
        const htmlPreloader = document.getElementById('spirit-preloader');
        if (htmlPreloader) {
          htmlPreloader.style.opacity = '0';
          htmlPreloader.style.display = 'none';
        }
      }
    }, 45000); // 45 seconds - after all other failsafes should have triggered

    return () => clearTimeout(ultimateFailsafeTimeout);
  }, [loaderRemoved, isShowingUpdateAnimation]);

  // Enable scroll position restoration
  useScrollRestoration();

  // Handle navigation from push notification clicks
  useNotificationNavigation();

  // Get invite code from URL
  const inviteCode = searchParams.get('code');

  // Handle loader completion - called when exit animation finishes
  const handleLoaderComplete = useCallback(() => {
    // Keep loader in DOM briefly to ensure smooth transition, then remove
    setTimeout(() => setLoaderRemoved(true), 100);
  }, []);

  // Determine if auth is ready (not in loading state)
  const isAuthReady = authState.status !== 'loading';

  // Render content - always rendered once auth is ready
  const renderContent = () => {
    // Still loading auth - don't render content yet
    if (authState.status === 'loading') {
      return null;
    }

    // Unauthenticated - show registration page
    if (authState.status === 'unauthenticated') {
      return (
        <Suspense fallback={<PageLoader />}>
          <RegisterPage inviteCode={inviteCode} />
        </Suspense>
      );
    }

    // Needs profile - show profile setup
    if (authState.status === 'needs_profile') {
      return (
        <Suspense fallback={<PageLoader />}>
          <ProfileSetupPage user={authState.user} inviteCode={authState.inviteCode} />
        </Suspense>
      );
    }

    // Needs MFA enrollment - show MFA setup screen
    if (authState.status === 'needs_mfa_enrollment') {
      return (
        <Suspense fallback={<PageLoader />}>
          <MfaRequiredScreen user={authState.user} profile={authState.profile} />
        </Suspense>
      );
    }

    // Needs reactivation - show welcome back screen
    if (authState.status === 'needs_reactivation') {
      return (
        <Suspense fallback={<PageLoader />}>
          <ReactivationScreen user={authState.user} profile={authState.profile} />
        </Suspense>
      );
    }

    // Authenticated - show main app with routed content
    return (
      <DonationProvider>
        <AppShell header={<Header />} navigation={<BottomNavigation />}>
          <Outlet />
        </AppShell>
        {/* Floating donation badge - renders based on config position */}
        <DonationBadge
          onDonateClick={() => {
            // [FUTURE] Open donation modal when implemented
          }}
        />
      </DonationProvider>
    );
  };

  return (
    <>
      {/* Content always renders underneath, visible immediately once auth is ready */}
      <div className={isAuthReady ? 'opacity-100' : 'opacity-0'}>{renderContent()}</div>

      {/* Loader renders on top with higher z-index - handles its own fade */}
      {/* Skip SpiritLoader when UpdateAnimation is showing */}
      {!loaderRemoved && !isShowingUpdateAnimation && (
        <SpiritLoader
          isReady={isAuthReady}
          onComplete={handleLoaderComplete}
          minDisplayTime={1800}
        />
      )}
    </>
  );
}

// Wrapper components to pass user/profile props to sections
function StudyRoute() {
  const { authState } = useAuth();
  if (authState.status !== 'authenticated') return null;
  return (
    <ErrorBoundary
      fallback={(error, reset) => (
        <FeatureErrorFallback featureName="Study" error={error} onReset={reset} />
      )}
    >
      <Suspense fallback={<PageLoader />}>
        <StudySection user={authState.user} profile={authState.profile} />
      </Suspense>
    </ErrorBoundary>
  );
}

function ChatRoute() {
  const { authState } = useAuth();
  if (authState.status !== 'authenticated') return null;
  return (
    <ErrorBoundary
      fallback={(error, reset) => (
        <FeatureErrorFallback featureName="Chat" error={error} onReset={reset} />
      )}
    >
      <Suspense fallback={<PageLoader />}>
        <ChatSection user={authState.user} profile={authState.profile} />
      </Suspense>
    </ErrorBoundary>
  );
}

function LibraryRoute() {
  const { authState } = useAuth();
  if (authState.status !== 'authenticated') return null;
  return (
    <ErrorBoundary
      fallback={(error, reset) => (
        <FeatureErrorFallback featureName="Library" error={error} onReset={reset} />
      )}
    >
      <Suspense fallback={<PageLoader />}>
        <LibrarySection user={authState.user} profile={authState.profile} />
      </Suspense>
    </ErrorBoundary>
  );
}

function PrayersRoute() {
  const { authState } = useAuth();
  if (authState.status !== 'authenticated') return null;
  return (
    <ErrorBoundary
      fallback={(error, reset) => (
        <FeatureErrorFallback featureName="Prayers" error={error} onReset={reset} />
      )}
    >
      <Suspense fallback={<PageLoader />}>
        <PrayersSection user={authState.user} profile={authState.profile} />
      </Suspense>
    </ErrorBoundary>
  );
}

function ProfileRoute() {
  const { authState } = useAuth();
  if (authState.status !== 'authenticated') return null;
  return (
    <ErrorBoundary
      fallback={(error, reset) => (
        <FeatureErrorFallback featureName="Profile" error={error} onReset={reset} />
      )}
    >
      <Suspense fallback={<PageLoader />}>
        <ProfileSection user={authState.user} profile={authState.profile} />
      </Suspense>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <VisualEffectsProvider>
      <UpdateAnimationProvider>
        <NavigationProvider>
          <Routes>
            {/* Main app layout with nested routes */}
            <Route element={<MainLayout />}>
              {/* Default redirect to study */}
              <Route index element={<IndexRedirect />} />

              {/* Main section routes */}
              <Route path="/study" element={<StudyRoute />} />

              {/* Chat routes with sub-tabs */}
              <Route path="/chat" element={<Navigate to="/chat/community" replace />} />
              <Route path="/chat/:tab" element={<ChatRoute />} />

              {/* Library route */}
              <Route path="/library" element={<LibraryRoute />} />

              {/* Prayers routes with type sub-tabs (default to "all" view) */}
              <Route path="/prayers" element={<Navigate to="/prayers/all" replace />} />
              <Route path="/prayers/:type" element={<PrayersRoute />} />

              {/* Profile route */}
              <Route path="/profile" element={<ProfileRoute />} />
            </Route>

            {/* Admin route (outside main layout) */}
            <Route path="/admin/*" element={<AdminRoute />} />

            {/* Public legal document hub and individual policy pages (no auth required) */}
            <Route
              path="/legal"
              element={
                <Suspense fallback={<PageLoader />}>
                  <LegalHubPage />
                </Suspense>
              }
            />
            <Route
              path="/legal/:slug"
              element={
                <Suspense fallback={<PageLoader />}>
                  <PolicyPage />
                </Suspense>
              }
            />

            {/* Legacy URLs kept alive for already-indexed links / Google verification */}
            <Route path="/privacy" element={<Navigate to="/legal/privacy-policy" replace />} />
            <Route path="/terms" element={<Navigate to="/legal/terms-of-service" replace />} />

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/study" replace />} />
          </Routes>
          <ToastContainer />
          {/* Update components - skip on admin page */}
          <NonAdminComponents />
        </NavigationProvider>
      </UpdateAnimationProvider>
    </VisualEffectsProvider>
  );
}
